import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook to manage page title based on current language and route
 * @param titleKey - The translation key for the page title (e.g., 'pageTitles.dashboard')
 * @param fallbackTitle - Optional fallback title if translation key is not found
 */
export const usePageTitle = (titleKey?: string, fallbackTitle?: string) => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (titleKey) {
      const translatedTitle = t(titleKey);
      const appName = t('appName');
      
      // Set document title with app name
      document.title = `${translatedTitle} | ${appName}`;
    } else if (fallbackTitle) {
      const appName = t('appName');
      document.title = `${fallbackTitle} | ${appName}`;
    } else {
      // Default title
      document.title = t('appName');
    }
  }, [titleKey, fallbackTitle, t, i18n.language]);
};

/**
 * Get page title key based on current route
 * @param pathname - The current pathname from router
 * @returns The translation key for the page title
 */
export const getPageTitleKey = (pathname: string): string | undefined => {
  // Remove trailing slash
  const path = pathname.replace(/\/$/, '');
  
  // Route to title key mapping
  const routeTitleMap: Record<string, string> = {
    // Main routes
    '/dashboard': 'pageTitles.dashboard',
    '/login': 'pageTitles.login',
    '/settings': 'pageTitles.settings',
    '/statistics': 'pageTitles.statistics',
    '/technical-support': 'pageTitles.technicalSupport',
    
    // Users & Permissions
    '/users': 'pageTitles.users',
    '/users/create': 'pageTitles.createUser',
    '/users/permissions': 'pageTitles.permissions',
    '/users/permissions/create': 'pageTitles.createPermission',
    '/users/permission-groups/create': 'pageTitles.createPermissionGroup',
    
    // Chat
    '/chat/conversations': 'pageTitles.chatConversations',
    '/chat/organizations': 'pageTitles.chatOrganizations',
    
    // Portal Sections - Imam Intro
    '/portal-sections/imam-intro': 'pageTitles.imamIntro',
    
    // Hadiths
    '/portal-sections/hadiths': 'pageTitles.hadiths',
    '/portal-sections/hadiths/hadiths-list': 'pageTitles.hadithsList',
    '/portal-sections/hadiths/hadiths': 'pageTitles.hadithsList',
    '/portal-sections/hadiths/hadiths-list/create': 'pageTitles.createHadith',
    '/portal-sections/hadiths/categories': 'pageTitles.hadithsCategories',
    '/portal-sections/hadiths/narrators': 'pageTitles.hadithsNarrators',
    '/portal-sections/hadiths/narrators/create': 'pageTitles.createHadithNarrator',
    '/portal-sections/hadiths/references': 'pageTitles.hadithsReferences',
    '/portal-sections/hadiths/references/create': 'pageTitles.createHadithReference',
    '/portal-sections/hadiths/hadis-parts': 'pageTitles.hadithsParts',
    
    // Prayers
    '/portal-sections/prayers': 'pageTitles.prayers',
    '/portal-sections/prayers/prayers-list': 'pageTitles.prayersList',
    '/portal-sections/prayers/prayers': 'pageTitles.prayersList',
    '/portal-sections/prayers/prayers-list/create': 'pageTitles.createPrayer',
    '/portal-sections/prayers/categories': 'pageTitles.prayersCategories',
    '/portal-sections/prayers/categories/create': 'pageTitles.createPrayerCategory',
    '/portal-sections/prayers/narrators': 'pageTitles.prayersNarrators',
    '/portal-sections/prayers/narrators/create': 'pageTitles.createPrayerNarrator',
    '/portal-sections/prayers/references': 'pageTitles.prayersReferences',
    '/portal-sections/prayers/references/create': 'pageTitles.createPrayerReference',
    '/portal-sections/prayers/prayer-parts': 'pageTitles.prayersParts',
    '/portal-sections/prayers/prayer-parts/create': 'pageTitles.createPrayerPart',
    
    // Library
    '/library': 'pageTitles.library',
    '/portal-sections/library': 'pageTitles.library',
    '/library/books': 'pageTitles.books',
    '/portal-sections/library/books': 'pageTitles.books',
    '/library/authors': 'pageTitles.authors',
    '/portal-sections/library/authors': 'pageTitles.authors',
    '/library/publishers': 'pageTitles.publishers',
    '/portal-sections/library/publishers': 'pageTitles.publishers',
    '/library/publishers/create': 'pageTitles.createPublisher',
    '/portal-sections/library/publishers/create': 'pageTitles.createPublisher',
    '/library/categories': 'pageTitles.categories',
    '/portal-sections/library/categories': 'pageTitles.categories',
    '/library/categories/create': 'pageTitles.createCategory',
    '/portal-sections/library/categories/create': 'pageTitles.createCategory',
    '/library/book-files': 'pageTitles.bookFiles',
    '/portal-sections/library/book-files': 'pageTitles.bookFiles',
    
    // Magazines
    '/portal-sections/magazines': 'pageTitles.magazines',
    '/portal-sections/magazines/magazines-list': 'pageTitles.magazinesList',
    '/portal-sections/magazines/magazines': 'pageTitles.magazinesList',
    '/portal-sections/magazines/magazines-list/create': 'pageTitles.createMagazine',
    
    // Gallery
    '/portal-sections/gallery': 'pageTitles.gallery',
    '/portal-sections/gallery/media': 'pageTitles.media',
    '/portal-sections/gallery/media/create': 'pageTitles.createMedia',
    '/portal-sections/gallery/categories': 'pageTitles.mediaCategories',
    '/portal-sections/gallery/categories/create': 'pageTitles.createMediaCategory',
    
    // News
    '/portal-sections/news': 'pageTitles.news',
    '/portal-sections/news/news-list': 'pageTitles.newsList',
    '/portal-sections/news/news': 'pageTitles.newsList',
    '/portal-sections/news/news-list/create': 'pageTitles.createNews',
    '/portal-sections/news/categories': 'pageTitles.newsCategories',
    
    // Special Files
    '/portal-sections/special-files': 'pageTitles.specialFiles',
    '/portal-sections/special-files/special-files-list': 'pageTitles.specialFilesList',
    '/portal-sections/special-files/special-files': 'pageTitles.specialFilesList',
    '/portal-sections/special-files/special-files-list/create': 'pageTitles.createSpecialFile',
    
    // Wiki
    '/portal-sections/wiki': 'pageTitles.wiki',
    '/portal-sections/wiki/wiki-list': 'pageTitles.wikiList',
    '/portal-sections/wiki/wiki': 'pageTitles.wikiList',
    '/portal-sections/wiki/wiki-list/create': 'pageTitles.createWiki',
    '/portal-sections/wiki/categories': 'pageTitles.wikiCategories',
    '/portal-sections/wiki/categories/create': 'pageTitles.createWikiCategory',
    '/portal-sections/wiki/authors': 'pageTitles.wikiAuthors',
    '/portal-sections/wiki/authors/create': 'pageTitles.createWikiAuthor',
    '/portal-sections/wiki/wiki-contents': 'pageTitles.wikiContents',
    
    // Heart Talk
    '/portal-sections/heart-talk': 'pageTitles.heartTalk',
    '/portal-sections/heart-talk/heart-talk-list': 'pageTitles.heartTalkList',
    '/portal-sections/heart-talk/categories': 'pageTitles.heartTalkCategories',
    '/portal-sections/heart-talk/authors': 'pageTitles.heartTalkAuthors',
    '/portal-sections/heart-talk/authors/create': 'pageTitles.createHeartTalkAuthor',
    
    // Contact Us
    '/portal-sections/contact-us': 'pageTitles.contactUs',
  };
  
  // Check exact match first
  if (routeTitleMap[path]) {
    return routeTitleMap[path];
  }
  
  // Check for dynamic routes (edit pages)
  if (path.includes('/edit')) {
    // Users
    if (path.startsWith('/users/') && path.includes('/permissions')) {
      return 'pageTitles.userPermissions';
    }
    if (path.startsWith('/users/permissions/')) {
      return 'pageTitles.editPermission';
    }
    if (path.startsWith('/users/permission-groups/')) {
      return 'pageTitles.editPermissionGroup';
    }
    if (path.startsWith('/users/')) {
      return 'pageTitles.editUser';
    }
    
    // Hadiths
    if (path.includes('/hadiths/hadiths-list/')) {
      return 'pageTitles.editHadith';
    }
    if (path.includes('/hadiths/narrators/')) {
      return 'pageTitles.editHadithNarrator';
    }
    if (path.includes('/hadiths/references/')) {
      return 'pageTitles.editHadithReference';
    }
    
    // Prayers
    if (path.includes('/prayers/prayers-list/')) {
      return 'pageTitles.editPrayer';
    }
    if (path.includes('/prayers/categories/')) {
      return 'pageTitles.editPrayerCategory';
    }
    if (path.includes('/prayers/narrators/')) {
      return 'pageTitles.editPrayerNarrator';
    }
    if (path.includes('/prayers/references/')) {
      return 'pageTitles.editPrayerReference';
    }
    if (path.includes('/prayers/prayer-parts/')) {
      return 'pageTitles.editPrayerPart';
    }
    
    // Library
    if (path.includes('/books/') && path.includes('/edit')) {
      return 'pageTitles.editBook';
    }
    if (path.includes('/authors/') && path.includes('/edit')) {
      return 'pageTitles.editAuthor';
    }
    if (path.includes('/publishers/') && path.includes('/edit')) {
      return 'pageTitles.editPublisher';
    }
    if (path.includes('/categories/') && path.includes('/edit')) {
      return 'pageTitles.editCategory';
    }
    
    // Magazines
    if (path.includes('/magazines/magazines-list/')) {
      return 'pageTitles.editMagazine';
    }
    
    // Gallery
    if (path.includes('/gallery/media/')) {
      return 'pageTitles.editMedia';
    }
    if (path.includes('/gallery/categories/')) {
      return 'pageTitles.editMediaCategory';
    }
    
    // News
    if (path.includes('/news/news-list/')) {
      return 'pageTitles.editNews';
    }
    
    // Special Files
    if (path.includes('/special-files/special-files-list/')) {
      return 'pageTitles.editSpecialFile';
    }
    
    // Wiki
    if (path.includes('/wiki/wiki-list/')) {
      return 'pageTitles.editWiki';
    }
    if (path.includes('/wiki/categories/')) {
      return 'pageTitles.editWikiCategory';
    }
    if (path.includes('/wiki/authors/')) {
      return 'pageTitles.editWikiAuthor';
    }
    
    // Heart Talk
    if (path.includes('/heart-talk/authors/')) {
      return 'pageTitles.editHeartTalkAuthor';
    }
  }
  
  if (/\/portal-sections\/hadiths\/narrators\/\d+$/.test(path)) {
    return 'pageTitles.hadithNarratorDetails';
  }
  if (/\/portal-sections\/hadiths\/references\/\d+$/.test(path)) {
    return 'pageTitles.hadithReferenceDetails';
  }

  return undefined;
};
